import { invariantResponse } from '@epic-web/invariant'
import {
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  redirect,
  json,
} from '@remix-run/node'
import { Form, useActionData, useLoaderData, useNavigate } from '@remix-run/react'
import { parse } from 'csv-parse/sync'
import { InventoryMappingPreview } from '#app/components/InventoryMappingPreview.tsx'
import {
  mapInventoryColumns,
  applyMapping,
  type ColumnMapping,
  type MappingResult
} from '#app/services/inventory-mapping.server.js'
import { InventoryService } from '#app/services/inventory.server.js'
import { getUserId, getCompanyId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import { cleanParsedData } from '#app/utils/inventory.server.js'
import { normalizeInvoiceDataBatch, formatNormalizedForDisplay } from '#app/utils/invoice-normalization.server.js'

export async function loader({ request }: LoaderFunctionArgs) {
  console.log('🔍 Map route loader called')
  // This route should only be accessed with CSV data in session
  // For now, we'll redirect back if no data is found
  return json({ message: 'Map route loaded' })
}

export async function action({ request }: ActionFunctionArgs) {
  console.log('🚀 Map route action called')
  const formData = await request.formData()
  const action = formData.get('_action')
  
  console.log('📝 Action received:', action)
  
  const userId = await getUserId(request)
  const companyId = await getCompanyId(request)
  
  console.log('👤 User ID:', userId, 'Company ID:', companyId)
  
  if (!userId || !companyId) {
    console.log('❌ Authentication failed')
    return json({ error: 'Authentication required' }, { status: 401 })
  }

  if (action === 'analyze') {
    console.log('🔍 Starting CSV analysis...')
    // Initial CSV analysis and mapping
    const csvFile = formData.get('csvFile')
    const inventoryGroupId = formData.get('inventoryGroupId') as string
    
    if (!csvFile || typeof csvFile === 'string') {
      return json({ error: 'No file uploaded' }, { status: 400 })
    }

    try {
      const fileText = await csvFile.text()
      const parsedData = parse(fileText, {
        columns: true,
        skip_empty_lines: true,
        delimiter: ',',
        trim: true,
      })

      if (!parsedData || parsedData.length === 0) {
        return json({ error: 'CSV file is empty or invalid' }, { status: 400 })
      }

      // ✨ NORMALIZE THE DATA BEFORE SHOWING TO USER
      console.log('📊 Normalizing invoice data...')
      const normalizedData = normalizeInvoiceDataBatch(parsedData)

      // Log normalization results
      const inventoryItems = normalizedData.filter(item => item.isInventoryItem)
      const skippedItems = normalizedData.filter(item => !item.isInventoryItem)
      console.log(`✅ Normalized ${inventoryItems.length} inventory items`)
      if (skippedItems.length > 0) {
        console.log(`⚠️ Skipped ${skippedItems.length} non-inventory items:`,
          skippedItems.map(item => item.description))
      }

      const headers = Object.keys(normalizedData[0])
      const mappingResult = await mapInventoryColumns(headers)

      return json({
        success: true,
        csvData: normalizedData.slice(0, 10), // Send NORMALIZED first 10 rows for preview
        mappingResult,
        totalRows: normalizedData.length,
        inventoryItemsCount: inventoryItems.length,
        skippedItemsCount: skippedItems.length,
        originalCsv: fileText, // Store for final processing
        inventoryGroupId: inventoryGroupId || null
      })

    } catch (error) {
      console.error('Error analyzing CSV:', error)
      return json({ error: 'Failed to analyze CSV file' }, { status: 500 })
    }
  }

  if (action === 'process') {
    // Final processing with confirmed mappings
    const originalCsv = formData.get('originalCsv')
    const mappingsJson = formData.get('mappings')
    const inventoryGroupId = formData.get('inventoryGroupId') as string
    
    if (!originalCsv || !mappingsJson) {
      return json({ error: 'Missing data for processing' }, { status: 400 })
    }

    try {
      const parsedData = parse(originalCsv, {
        columns: true,
        skip_empty_lines: true,
        delimiter: ',',
        trim: true,
      })

      const mappings: ColumnMapping[] = JSON.parse(mappingsJson.toString())
      const mappedData = applyMapping(parsedData, mappings)
      const cleanedData = cleanParsedData(mappedData)
      
      // DEBUG: Log first few mapped items to see the structure
      console.error('🔍 MAPPED DATA SAMPLE (first 3 items):')
      cleanedData.slice(0, 3).forEach((item, index) => {
        console.error(`Item ${index + 1}:`, {
          Product: item.Product,
          Size: item.Size,
          Measure: item.Measure,
          'All keys': Object.keys(item)
        })
      })

      const unrecognizedItems = await InventoryService.bulk.createOrUpdateItems(
        cleanedData,
        companyId,
        userId,
        inventoryGroupId || undefined
      )

      if (unrecognizedItems && unrecognizedItems.length > 0) {
        // Format and store unrecognized items
        const formattedUnrecognizedItems = unrecognizedItems
          .filter(item => item.itemName && item.companyId && item.userId)
          .map(item => ({
            itemName: item.itemName!,
            unit: item.unit || '',
            companyId: item.companyId!,
            userId: item.userId!,
            createdAt: new Date(),
            updatedAt: new Date(),
            inventoryItemId: item.inventoryItemId!,
          }))

        if (formattedUnrecognizedItems.length > 0) {
          await prisma.unrecognizedItem.createMany({
            data: formattedUnrecognizedItems,
          })
        }

        return redirect('/add-inventory/missing')
      }

      return redirect('/inventory')
      
    } catch (error) {
      console.error('Error processing mapped CSV:', error)
      return json({ error: 'Failed to process inventory data' }, { status: 500 })
    }
  }

  return json({ error: 'Invalid action' }, { status: 400 })
}

export default function InventoryMapping() {
  const data = useActionData<typeof action>()
  const navigate = useNavigate()

  // If we have analysis results, show the mapping interface
  if (data?.success && data.csvData && data.mappingResult) {
    const handleMappingChange = (mappings: ColumnMapping[]) => {
      // This will be handled by the component state
      // The final mappings will be sent when user confirms
    }

    const handleConfirm = () => {
      // Get the current mappings from the component and submit
      const form = document.getElementById('processForm') as HTMLFormElement
      if (form) {
        form.submit()
      }
    }

    const handleCancel = () => {
      navigate('/add-inventory')
    }

    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Map Your Inventory Spreadsheet</h1>
          <p className="text-gray-600 mt-2">
            Found {data.totalRows} rows in your spreadsheet. Please confirm the column mappings below.
          </p>
        </div>

        <InventoryMappingPreview
          csvData={data.csvData}
          mappingResult={data.mappingResult}
          onMappingChange={handleMappingChange}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        {/* Hidden form for final processing */}
        <Form id="processForm" method="post" style={{ display: 'none' }}>
          <input type="hidden" name="_action" value="process" />
          <input type="hidden" name="originalCsv" value={data.originalCsv} />
          <input type="hidden" name="inventoryGroupId" value={data.inventoryGroupId || ''} />
          <input 
            type="hidden" 
            name="mappings" 
            id="mappingsInput"
            value={JSON.stringify(data.mappingResult.mappings)} 
          />
        </Form>
      </div>
    )
  }

  // Initial upload form or route test
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">
          CSV Column Mapping
        </h2>
        <p className="mt-4 text-gray-600">
          Route is working! Upload a CSV file to test the mapping functionality.
        </p>
        
        <div className="mb-4 p-4 bg-blue-100 rounded">
          <p>Debug: Route loaded successfully</p>
        </div>

        <div className="mt-10 flex items-center justify-center gap-x-6">
          <Form
            className="w-full max-w-[500px]"
            method="post"
            encType="multipart/form-data"
          >
            <input type="hidden" name="_action" value="analyze" />
            <div className="mb-8">
              <input
                type="file"
                name="csvFile"
                className="block w-full rounded-md border-0 py-1.5 text-black shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-secondary-orange sm:text-sm sm:leading-6"
                accept=".csv"
                required
              />
            </div>
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              Test CSV Upload
            </button>
          </Form>
        </div>

        {data?.error && (
          <div className="mt-4 text-red-600">
            <p>Error: {data.error}</p>
          </div>
        )}
        
        {data?.success && (
          <div className="mt-4 text-green-600">
            <p>Success! Found {data.totalRows} rows</p>
          </div>
        )}
      </div>
    </div>
  )
}